import React from 'react'
import { FilePond, registerPlugin } from 'react-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Paper, Tooltip, Typography } from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import PaidIcon from '@mui/icons-material/Paid'
import {
  useCancelDistributionInstructionMutation,
  useConfirmInstructionPaymentMutation, useDistributionInstructionApiParseBillingCSVFileMutation,
  useGetAssetDistributionsForBillingMonthQuery
} from '../../../../api-services/asset-holdings/distributionInstructionApi'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import { NumberFormatter } from '../../../../utility/formatters/NumberFormatter'
import AssetDistributionStatusChip from '../../../../views/components/chips/AssetDistributionStatusChip'
import MUIDataTable, { type MUIDataTableOptions, type SelectableRows } from 'mui-datatables'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectCancelInstructionButtons, selectCSVFileSelected,
  selectInstructionIds, selectIsCSVParsing,
  setCancelButtonsEnabled, setCSVParsing, setCSVFileSelected,
  setInstructionIds
} from './editDistributionInstructionsSlice'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  type AssetDistributionInstructionDetail
} from '../../../../api-schema-types/asset-holdings/DistributionInstructionDomainDtos'
import ButtonLoadingComponent from '../../../../views/components/shared/ButtonLoadingComponent'
import Refresh from '@mui/icons-material/Refresh'

registerPlugin(FilePondPluginFileValidateType)

const MonthlyBillingRunPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const instructionIds: number[] = useAppSelector<number[]>(selectInstructionIds)
  const cancelButtonsEnabled: boolean = useAppSelector<boolean>(selectCancelInstructionButtons)
  const csvFileSelected: boolean = useAppSelector<boolean>(selectCSVFileSelected)
  const isCSVParsing: boolean = useAppSelector<boolean>(selectIsCSVParsing)
  const goTo = (location: string): void => {
    history.push(location)
  }

  let monthlyRunId: number | null = null
  // Initialise params
  if (Boolean(params.billingRunMonthlyId) && monthlyRunId !== '') {
    monthlyRunId = params.billingRunMonthlyId
  }

  const skipQuery = monthlyRunId === null
  let pond: FilePond | null

  const getDistributionInstructionsApiResult = useGetAssetDistributionsForBillingMonthQuery({ billingRunMonthlyId: monthlyRunId }, { skip: skipQuery })
  const refetchGetDistributionInstructionsApiResult = getDistributionInstructionsApiResult.refetch
  const [apiConfirmPaymentReceived] = useConfirmInstructionPaymentMutation()
  const [apiCancelDistributionInstructionTrigger] = useCancelDistributionInstructionMutation()
  const [apiParseCSVFileContentTrigger] = useDistributionInstructionApiParseBillingCSVFileMutation()

  const handleRefetch = async (): Promise<void> => {
    await refetchGetDistributionInstructionsApiResult()
  }

  const renderInstructionAction = (instructionId: number): React.ReactElement => {
    if (getDistributionInstructionsApiResult.isSuccess) {
      const instruction = getDistributionInstructionsApiResult.data.instructions.find((i) => i.id === instructionId)
      if (instruction === undefined) {
        return <></>
      }
      if (isCancelInstructionEnabled(instruction)) {
        return (
          <Button onClick={() => {
            handleCancelDistributionInstruction(instruction)
          }} type={'button'} variant={'outlined'} color={'error'} disabled={cancelButtonsEnabled}>
            <DeleteForeverIcon/>
            Cancel
          </Button>
        )
      } else {
        return (
          <Button onClick={() => {
            goTo(`/app/distributions/instruction/${instructionId}`)
          }} type={'button'} variant={'text'}>
            View
          </Button>
        )
      }
    }
    return <></>
  }

  const ratedUsagesColumns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'projectId',
      label: 'Project Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'projectName',
      label: 'Project',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billableCurrencyId',
      label: 'Billable Currency Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billableCurrency',
      label: 'Billable Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={value}/>
          )
        }
      }
    },
    {
      name: 'billingType',
      label: 'Billing Type',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'totalRatedAmount',
      label: 'Total Rated Amount',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'electricityConsumed',
      label: 'Electricity Consumed',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'usageTotal',
      label: 'Usage Total',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'totalAdminFee',
      label: 'Total Admin Fee',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'fxRate',
      label: 'Fx Rate',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<AssetDistributionStatusChip itemStatus={value}/>)
        }
      }
    }
  ]
  const distributionsColumns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'projectId',
      label: 'Project Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'projectName',
      label: 'Project',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'currencyId',
      label: 'Currency Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'currencyCode',
      label: 'Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={value}/>
          )
        }
      }
    },
    {
      name: 'numberCellOwnerAgreements',
      label: '# COAs',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'numberOfCells',
      label: '# Cells',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'amountToDistribute',
      label: 'Amount To Distribute',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'amountPerCell',
      label: 'Amount Per Cell',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<AssetDistributionStatusChip itemStatus={value}/>)
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          console.log()
          return (
            renderInstructionAction(tableMeta.rowData[0])
          )
        }
      }
    }
  ]

  const optionsRatedUsages: MUIDataTableOptions = {
    expandableRows: false,
    enableNestedDataAccess: '.',
    selectableRowsHideCheckboxes: false,
    selectableRows: 'none' as SelectableRows,
    expandableRowsOnClick: false
  }

  const optionsDistributionInstruction: MUIDataTableOptions = {
    expandableRows: false,
    enableNestedDataAccess: '.',
    selectableRows: 'multiple',
    selectableRowsOnClick: true,
    filterType: 'dropdown',
    rowsPerPage: 10,
    customToolbarSelect: selectedRows => (
      <Tooltip title="Mark Instructions as Payment Received">
        <Button
          onClick={() => {
            handleMarkPaymentReceived()
          }}
        >
          <PaidIcon/>Payment Received
        </Button>
      </Tooltip>
    ),
    textLabels: {
      selectedRows: {
        text: 'AWAITING PAYMENT Asset Distribution Instructions(s) selected',
        delete: 'Delete',
        deleteAria: 'Delete Selected Rows'
      }
    },
    isRowSelectable: dataIndex => {
      if (getDistributionInstructionsApiResult.isSuccess) {
        const instruction = getDistributionInstructionsApiResult.data.instructions[dataIndex]
        if (!isCancelInstructionEnabled(instruction)) {
          return (getDistributionInstructionsApiResult.data.instructions[dataIndex].status === 'AWAITING_PAYMENT')
        }
      }
      return false
    },
    onRowSelectionChange: (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[]) => {
      console.log(currentRowsSelected, allRowsSelected)
      const selectedInstructions: number[] = []
      if (getDistributionInstructionsApiResult.isSuccess) {
        for (const selectedRow of allRowsSelected) {
          selectedInstructions.push(getDistributionInstructionsApiResult.data.instructions[selectedRow.dataIndex].id)
        }
        dispatch(setInstructionIds(selectedInstructions))
      }
    }
  }

  const isPaymentReceivedEnabled = (): boolean => {
    return !(instructionIds.length > 0)
  }

  const isCancelInstructionEnabled = (instruction: AssetDistributionInstructionDetail): boolean => {
    return (instruction.status === 'AWAITING_PAYMENT' && instruction.amountToDistribute === 0 && instruction.amountPerCell === 0)
  }
  const handleMarkPaymentReceived = (): void => {
    if (!isPaymentReceivedEnabled()) {
      apiConfirmPaymentReceived({ assetDistributionInstructionIds: instructionIds })
        .unwrap()
        .then(() => {
          dispatch(setNotification({
            open: true,
            severity: 'success',
            message: 'Instruction\'s have been marked as Payment Received and are being processed.'
          }))
        })
        .catch((err) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
    dispatch(setInstructionIds([]))
    goTo('/app/distributions/run')
  }
  const handleCancelDistributionInstruction = (instruction: AssetDistributionInstructionDetail): void => {
    dispatch(setCancelButtonsEnabled(true))
    const choice = window.confirm(
      'Are you sure you want to Cancel?'
    )
    if (choice) {
      if (getDistributionInstructionsApiResult.isSuccess) {
        if (isCancelInstructionEnabled(instruction)) {
          apiCancelDistributionInstructionTrigger({ assetDistributionInstructionIds: [instruction.id] })
            .unwrap()
            .then(async () => {
              await handleRefetch()
              dispatch(setNotification({
                open: true,
                severity: 'success',
                message: 'Instruction has been transitioned to CANCELLED.'
              }))
              dispatch(setCancelButtonsEnabled(false))
            })
            .catch((err) => {
              dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
              dispatch(setCancelButtonsEnabled(false))
            })
        }
      }
    } else {
      dispatch(setCancelButtonsEnabled(false))
    }
  }

  const handleParseCSVFile = async (): Promise<void> => {
    try {
      if (pond != null) {
        dispatch(setCSVParsing(true))
        const inputFile = pond.getFile().file
        const contents = await inputFile.text()
        apiParseCSVFileContentTrigger({ billingRunMonthId: monthlyRunId, csvContent: contents })
          .unwrap()
          .then((triggerResult) => {
            dispatch(setNotification({
              open: true,
              severity: 'success',
              message: 'Billing CSV content\'s have been parsed and the DistributionInstructions are being created, please check back in a minute or so.\n'
            }))
            goTo('/app/distributions/run')
          })
          .catch((err: any) => {
            dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
          })
        dispatch(setCSVParsing(false))
      }
    } catch (e) {
      dispatch(setCSVParsing(false))
    }
  }

  return (
    <>
    {getDistributionInstructionsApiResult.isLoading && <LoadingComponent />}
    {(getDistributionInstructionsApiResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(getDistributionInstructionsApiResult.error)} />}
    { getDistributionInstructionsApiResult.isSuccess && (
      <>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ p: 1, flexGrow: 1 }}>
            <Typography variant={'h5'}>Monthly Billing Run [{monthlyRunId}] {getDistributionInstructionsApiResult.data.details.billingMonth}/{getDistributionInstructionsApiResult.data.details.billingYear} ({getDistributionInstructionsApiResult.data.details.billingPeriodStart} - {getDistributionInstructionsApiResult.data.details.billingPeriodEnd})</Typography>
          </Box>
          <Box sx={{ p: 1 }}>
            <Button variant={'outlined'} type="button" onClick={handleRefetch}>
              <Refresh/>
            </Button>
          </Box>
          <Box sx={{ p: 1 }}>
            <Button variant={'text'} type='button' onClick={() => {
              goTo('/app/distributions/run')
            }}>
              <ArrowBackIosIcon />
            </Button>
          </Box>
        </Box>
        <Paper>
          { getDistributionInstructionsApiResult.data.instructions.length === 0 && (
          <Box sx={{ p: 4, mt: 3 }}>
            <Typography variant={'body1'} sx={{ mb: 1 }}>
              Upload CSV File:
            </Typography>
            <FilePond
              ref={ref => (pond = ref)}
              allowMultiple={false}
              maxFiles={1}
              acceptedFileTypes={['text/csv']}
              onupdatefiles={fileItems => {
                dispatch(setCSVFileSelected(false))
                // setCsvFileParseSuccess(false)
                if (fileItems.length > 0) {
                  dispatch(setCSVFileSelected(true))
                } else {
                  dispatch(setCSVFileSelected(false))
                }
              }}
            />
            <Button
              type="button"
              variant={'contained'}
              onClick={handleParseCSVFile}
              disabled={!csvFileSelected}
              sx={{ mr: 3 }}
            >
              Parse CSV file contents and generate instructions
               { isCSVParsing && <ButtonLoadingComponent /> }
            </Button>
          </Box>
          )}
          <Box sx={{ p: 4, mt: 3 }}>
            <MUIDataTable
              title={'Distribution Instructions'}
              data={getDistributionInstructionsApiResult.data.instructions}
              columns={distributionsColumns}
              options={optionsDistributionInstruction}>
            </MUIDataTable>
          </Box>
          <Box sx={{ p: 4, mt: 3 }}>
            <MUIDataTable
              title={'Asset Rated Usages'}
              data={getDistributionInstructionsApiResult.data.ratedUsages}
              columns={ratedUsagesColumns}
              options={optionsRatedUsages}>
            </MUIDataTable>
          </Box>

        </Paper>
      </>
    )}
    </>
  )
}

export default MonthlyBillingRunPage
